import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Auto-applies the editorial "Task Management" title accent to every page:
 * the last word of any <h1> inside <main> is wrapped in <em> so it picks up
 * the global Fraunces serif italic styling, matching the task module aesthetic.
 *
 * Skipped when the heading already contains an <em>/<i> child or has fewer
 * than two words, or when the last token isn't plain alphabetic text.
 */
export function useTitleAccent() {
  const { pathname } = useLocation();

  useEffect(() => {
    const apply = () => {
      const headings = document.querySelectorAll<HTMLElement>(
        "main h1, main h2.text-2xl, main h2.text-3xl, main h2.text-4xl"
      );
      headings.forEach((h) => {
        if (h.dataset.themedAccent === "true") return;
        if (h.querySelector("em, i")) {
          h.dataset.themedAccent = "true";
          return;
        }
        const raw = (h.textContent || "").trim();
        if (!raw) return;
        const words = raw.split(/\s+/);
        if (words.length < 2) return;

        const last = words[words.length - 1];
        // Skip if last token is mostly emoji/punctuation/symbol
        if (!/[A-Za-z]{2,}/.test(last)) return;

        const head = words.slice(0, -1).join(" ");
        const safeHead = document.createTextNode(head + " ");
        const em = document.createElement("em");
        em.textContent = last;
        h.replaceChildren(safeHead, em);
        h.dataset.themedAccent = "true";
      });
    };

    // Run once after mount, and again shortly after to catch async-rendered titles
    const t1 = window.setTimeout(apply, 30);
    const t2 = window.setTimeout(apply, 200);
    const t3 = window.setTimeout(apply, 600);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [pathname]);
}
